import { useCallback, useState } from 'react';
import type { Message } from '../types/chat';
import { generateUUID } from '../utils/uuid';
import { useChatMessages } from './useChatMessages';
import { useChatbot } from '../contexts/ChatbotContext';
import { detectProductMentions } from '../utils/productDetection';
import { formatOpenAIResponseWithProducts } from '../utils/textFormatting';
import { useConversationContext } from './useConversationContext';
import { useCustomTexts } from './useCustomTexts';

// Define a type for the streaming response chunk that matches OpenAI's structure
interface StreamingResponseChunk {
  choices: Array<{
    delta?: {
      content?: string | null;
    };
    index?: number;
    finish_reason?: string | null;
  }>;
  id: string;
  object: string;
  created: number;
  model: string;
}

// Fonction utilitaire pour ajouter un délai
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export function useStreamingResponse() {
  const [isStreaming, setIsStreaming] = useState(false);
  const [isProcessingProducts, setIsProcessingProducts] = useState(false);
  const { addMessage, updateLastBotMessage } = useChatMessages();
  const { chatbotPublicId } = useChatbot();

  /**
   * Process a streaming response from the API
   * @param response The async iterable response from the API
   * @param onMessageComplete Optional callback to be called when the message is complete
   * @returns The accumulated text from the response
   */
  const streamResponse = useCallback(async (
    response: AsyncIterable<StreamingResponseChunk>,
    onMessageComplete?: (message: Message) => void
  ) => {
    setIsStreaming(true);
    let accumulatedText = '';
    let messageId = generateUUID();
    let lastUpdateTime = Date.now();

    try {
      const botMessage: Message = {
        id: messageId,
        type: 'bot',
        content: '',
        timestamp: new Date().toISOString()
      };
      addMessage(botMessage);

      // Phase 1: Streaming - juste le texte sans détection de produits pendant le streaming
      for await (const chunk of response) {
        const content = chunk.choices[0]?.delta?.content || '';
        if (content) {
          // Assurer un délai minimal entre les mises à jour pour la fluidité
          const now = Date.now();
          const timeSinceLastUpdate = now - lastUpdateTime;
          if (timeSinceLastUpdate < 20) {
            await delay(20 - timeSinceLastUpdate);
          }
          
          accumulatedText += content;
          updateLastBotMessage(accumulatedText);
          lastUpdateTime = Date.now();
        }
      }
      
      // Important: signaler immédiatement la fin du streaming AVANT de commencer la détection de produits
      // Cela améliore l'expérience utilisateur en évitant le clignotement
      setIsStreaming(false);
      
      // Pas de détection de produits si pas de texte ou pas d'ID chatbot
      if (!accumulatedText || !chatbotPublicId) {
        if (onMessageComplete) {
          onMessageComplete({
            ...botMessage,
            content: accumulatedText
          });
        }
        return accumulatedText;
      }
      
      // Phase 2: Détection et enrichissement avec les produits
      // Démarrage de la détection de produits avec un léger délai pour éviter le clignotement
      await delay(50); // Court délai pour une transition fluide
      
      console.log("Streaming finished, beginning product detection");
      setIsProcessingProducts(true);
      
      try {
        const mentionedProducts = await detectProductMentions(accumulatedText, chatbotPublicId);
        
        if (mentionedProducts?.length > 0) {
          console.log(`Detected ${mentionedProducts.length} products, enriching content`);
          const enrichedContent = await formatOpenAIResponseWithProducts(
            accumulatedText,
            chatbotPublicId,
            mentionedProducts
          );
          
          // Mise à jour finale avec le contenu enrichi
          // La mise à jour se fait avec hasProducts=true pour indiquer que le contenu contient des produits
          updateLastBotMessage(accumulatedText, enrichedContent, true);
        } else {
          console.log("No products detected in message");
        }
      } catch (err) {
        console.error('Error in product detection:', err);
      } finally {
        setIsProcessingProducts(false);
      }

      if (onMessageComplete) {
        onMessageComplete({
          ...botMessage,
          content: accumulatedText
        });
      }

      return accumulatedText;
    } catch (err) {
      console.error('Error streaming response:', err);
      const { errorMessage } = useCustomTexts.getState();
      updateLastBotMessage(errorMessage);
      setIsStreaming(false);
      setIsProcessingProducts(false);
      throw err;
    }
  }, [addMessage, updateLastBotMessage, chatbotPublicId]);

  return { streamResponse, isStreaming, isProcessingProducts };
}